import React, { useState } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HPButton from "@components/common/Button";
import HPInput from "@components/common/Input";
import type { VisualizationBase, VisualizationBaseCondition } from "@models/VisualizationBase";
import BaseConditionBuilder from "./BaseConditionBuilder";
import BaseConditionSummaryCard from "./BaseConditionSummaryCard";
import DisableCustomBaseConfirmDialog from "./DisableCustomBaseConfirmDialog";

interface BaseSectionProps {
  datasetId: string | null;
  isDisabled?: boolean;
  base: VisualizationBase;
  onEnableCustomBase: () => void;
  onDisableCustomBase: () => void;
  onNameChange: (name: string) => void;
  onSaveCondition: (condition: VisualizationBaseCondition, isEditing: boolean) => void;
  onRemoveCondition: (id: string) => void;
}

function createBlankCondition(): VisualizationBaseCondition {
  return {
    id: crypto.randomUUID(),
    questionId: "",
    questionType: "single",
    variableIds: [],
    valueIds: [],
  };
}

const AndDivider: React.FC = () => (
  <Divider>
    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
      AND
    </Typography>
  </Divider>
);

const BaseSection: React.FC<BaseSectionProps> = ({
  datasetId,
  isDisabled,
  base,
  onEnableCustomBase,
  onDisableCustomBase,
  onNameChange,
  onSaveCondition,
  onRemoveCondition,
}) => {
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [editingConditionId, setEditingConditionId] = useState<string | null>(null);
  const [draftCondition, setDraftCondition] = useState<VisualizationBaseCondition | null>(null);

  const isBuilderOpen = draftCondition !== null;
  const hasCustomBaseData = !!base.name.trim() || base.conditions.length > 0;

  const resetBuilder = () => {
    setEditingConditionId(null);
    setDraftCondition(null);
  };

  const handleModeChange = (mode: "all" | "custom") => {
    if (mode === "custom") {
      onEnableCustomBase();
      return;
    }
    if (hasCustomBaseData) {
      setShowDisableConfirm(true);
    } else {
      onDisableCustomBase();
    }
  };

  const handleConfirmDisable = () => {
    setShowDisableConfirm(false);
    onDisableCustomBase();
    resetBuilder();
  };

  const openAddBuilder = () => {
    setEditingConditionId(null);
    setDraftCondition(createBlankCondition());
  };

  const openEditBuilder = (condition: VisualizationBaseCondition) => {
    setEditingConditionId(condition.id);
    setDraftCondition({ ...condition });
  };

  const handleSaveDraft = () => {
    if (!draftCondition) return;
    onSaveCondition(draftCondition, editingConditionId !== null);
    resetBuilder();
  };

  const handleDeleteCondition = (id: string) => {
    onRemoveCondition(id);
    if (editingConditionId === id) resetBuilder();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Base
      </Typography>

      <RadioGroup
        value={base.enabled ? "custom" : "all"}
        onChange={(e) => handleModeChange(e.target.value as "all" | "custom")}
        sx={{ mt: 1 }}
      >
        <FormControlLabel
          value="all"
          control={<Radio size="small" />}
          label="All eligible respondents"
          disabled={isDisabled}
        />
        <FormControlLabel
          value="custom"
          control={<Radio size="small" />}
          label="Custom base"
          disabled={isDisabled || !datasetId}
        />
      </RadioGroup>

      {base.enabled && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: 1,
          }}
        >
          <HPInput
            label="Base Name*"
            value={base.name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Enter a name for this base"
            disabled={isDisabled}
          />

          {base.conditions.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {base.conditions.map((condition, index) => (
                <React.Fragment key={condition.id}>
                  {index > 0 && <AndDivider />}
                  <BaseConditionSummaryCard
                    datasetId={datasetId as string}
                    condition={condition}
                    index={index}
                    isDimmed={isBuilderOpen && editingConditionId !== condition.id}
                    isHighlighted={editingConditionId === condition.id}
                    disabled={isDisabled}
                    onEdit={() => openEditBuilder(condition)}
                    onDelete={() => handleDeleteCondition(condition.id)}
                  />
                </React.Fragment>
              ))}
            </Box>
          )}

          {isBuilderOpen && draftCondition ? (
            <>
              {base.conditions.length > 0 && editingConditionId === null && (
                <AndDivider />
              )}
              <BaseConditionBuilder
                datasetId={datasetId as string}
                condition={draftCondition}
                excludedQuestionIds={base.conditions
                  .filter((c) => c.id !== draftCondition.id)
                  .map((c) => c.questionId)
                  .filter(Boolean)}
                isDisabled={isDisabled}
                isEditing={editingConditionId !== null}
                onChange={setDraftCondition}
                onCancel={resetBuilder}
                onSave={handleSaveDraft}
              />
            </>
          ) : (
            <HPButton
              ariaLabel="Add base condition"
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openAddBuilder}
              disabled={isDisabled || !datasetId}
            >
              Add Condition
            </HPButton>
          )}
        </Box>
      )}

      <DisableCustomBaseConfirmDialog
        open={showDisableConfirm}
        onCancel={() => setShowDisableConfirm(false)}
        onConfirm={handleConfirmDisable}
      />
    </Box>
  );
};

export default BaseSection;
